import { Box, Grid, MenuItem, Select } from "@mui/material";
import * as React from "react";
import { CheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/CheckboxDropdown";
import { TreeCheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/TreeCheckboxDropdown";
import { IIdTitle } from "ui/Scripts/source/models/common/idTitle";
import { IIdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { ITaskDataRow, TaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { ITaskDataRowCol } from "ui/Scripts/source/models/task/taskDataRowCol";
import { Translations } from "ui/Scripts/source/models/translations";
import { SaveDataObj } from "source/framework/saveData";
import { IReportItem } from "source/models/report/reportItem";
import { FilterButtons } from "source/components/framework/Filters/FilterButtons";
import { FilterSearchField } from "source/components/framework/Filters/FilterSearchField";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { TaskDynamicForm } from "source/components/task/TaskDynamicForm/TaskDynamicForm";
import { ISaveReportFilters } from "source/components/report/PageNavMenuItemReportList/SaveReportDialog";

export interface IRecordPageListReportFilterPanelProp {
    classes?: string;
    report: IReportItem;
    units: IIdTitleNode[];
    activityFilters: IIdTitle[];
    jobTitles: IIdTitle[];
    employeeTypes: IIdTitle[];
    selectedActivityFilterId: string;
    selectedEmployeeTypeIds: string[];
    selectedJobTitleIds: string[];
    selectedUnitIds: string[];
    canRemoveSharedReports: boolean;
    onSaveReport: (saveAs: boolean, onGetReportSaveFilters: () => ISaveReportFilters) => void;
    onRemoveReport: (filterId: string) => void;
    onOk: (closeDialog: boolean, selectedActivityFilterId: string, selectedEmployeeTypeIds: string[], selectedJobTitleIds: string[], selectedUnitIds: string[], parameters: ITaskDataRow) => ISaveReportFilters;
    onCancel: () => void;
}

export interface IRecordPageListReportFilterPanelState {
    selectedEmployeeTypeIds: string[];
    selectedJobTitleIds: string[];
    selectedUnitIds: string[];
    selectedActivityFilterId: string;
    extraParameters: ITaskDataRow;
    prevReportHash: string;
}

export class RecordPageListReportFilterPanel extends React.Component<IRecordPageListReportFilterPanelProp, IRecordPageListReportFilterPanelState> {
    private orgStateHash: string = "";

    getStateFromProps = (props: IRecordPageListReportFilterPanelProp): IRecordPageListReportFilterPanelState => {
        return {
            selectedActivityFilterId: props.selectedActivityFilterId,
            selectedEmployeeTypeIds: props.selectedEmployeeTypeIds.slice(0),
            selectedJobTitleIds: props.selectedJobTitleIds.slice(0),
            selectedUnitIds: props.selectedUnitIds.slice(0),
            extraParameters: props.report?.taskType && props.report?.taskDataRow ? new TaskDataRow(props.report.taskDataRow) : null,
            prevReportHash: props.report?.getHash() ?? ""
        };
    };

    constructor(props: IRecordPageListReportFilterPanelProp) {
        super(props);
        this.state = this.getStateFromProps(props);
        this.orgStateHash = this.getSaveDataFromState(props, this.state).hash;
    }

    componentDidUpdate(prevProps: IRecordPageListReportFilterPanelProp, prevState: IRecordPageListReportFilterPanelState): void {
        const props = this.props;
        if (!prevProps || this.state.prevReportHash === (props.report?.getHash() ?? "")) return;
        const state = this.getStateFromProps(props);
        this.setState(state);
        this.orgStateHash = this.getSaveDataFromState(props, state).hash;
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "activityFilterId") {
            this.setState({ selectedActivityFilterId: value });
        }
    };

    handleChangeUnitIds = (selectedUnitIds: string[]) => {
        this.setState({
            selectedUnitIds
        });
    };

    handleChangeEmployeeTypeIds = (selectedEmployeeTypeIds: string[]) => {
        this.setState({
            selectedEmployeeTypeIds
        });
    };

    handleChangeJobTitleIds = (selectedJobTitleIds: string[]) => {
        this.setState({
            selectedJobTitleIds
        });
    };

    handleRefreshParameters = (taskDataRowCols: ITaskDataRowCol[]) => {
        const state = this.state;
        if (!state.extraParameters) return;
        const extraParameters = new TaskDataRow(state.extraParameters);
        extraParameters.cols = taskDataRowCols;
        this.setState({ extraParameters });
    };

    getSaveDataFromState = (props: IRecordPageListReportFilterPanelProp, state: IRecordPageListReportFilterPanelState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("selectedActivityFilterId", state.selectedActivityFilterId ? state.selectedActivityFilterId : null);
        result.append("selectedEmployeeTypeIds", state.selectedEmployeeTypeIds);
        result.append("selectedUnitIds", state.selectedUnitIds);
        result.append("selectedJobTitleIds", state.selectedJobTitleIds);
        if (props.report?.taskType && props.report?.taskDataRow) {
            result.append("parameters", state.extraParameters.cols.map(i => state.extraParameters.getTaskDataRowColUiValue(i.field, props.report.taskType)));
        }
        return result;
    };

    handleSaveReport = async (saveAs: boolean) => {
        const props = this.props;
        const state = this.state;
        if (!saveAs) {
            props.onOk(false, state.selectedActivityFilterId, state.selectedEmployeeTypeIds, state.selectedJobTitleIds, state.selectedUnitIds, this.state.extraParameters);
            await this.props.onSaveReport(false, null);
        } else {
            await this.props.onSaveReport(true, () => {
                return props.onOk(false, state.selectedActivityFilterId, state.selectedEmployeeTypeIds, state.selectedJobTitleIds, state.selectedUnitIds, this.state.extraParameters);
            });
        }
    };

    handleClear = () => {
        const props = this.props;
        const state = this.state;
        const extraParameters = state.extraParameters ? new TaskDataRow(state.extraParameters) : null;
        if (extraParameters) {
            extraParameters.initializeToTaskType(props.report.taskType);
        }
        this.setState({
            selectedActivityFilterId: props.activityFilters[1].id,
            selectedEmployeeTypeIds: [props.employeeTypes[0].id],
            selectedJobTitleIds: [props.jobTitles[0].id],
            selectedUnitIds: [props.units[0].id],
            extraParameters
        });
    };

    handleSave = async () => {
        const props = this.props;
        const state = this.state;
        props.onOk(true, state.selectedActivityFilterId, state.selectedEmployeeTypeIds, state.selectedJobTitleIds, state.selectedUnitIds, this.state.extraParameters);
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <Box display="flex" flexDirection="column" justifyContent="space-between" flexGrow={2} width="100%">
                <Box display="flex" flexDirection="column" flexGrow={2}>
                    <FilterSearchField
                        filter={props.report}
                        canRemoveSharedFilters={props.canRemoveSharedReports}
                        isReport={true}
                        onSaveFilter={this.handleSaveReport}
                        onRemoveFilter={props.onRemoveReport}
                    />
                    <GridRow>
                        <Grid item sm={12}>
                            <FormField title={Translations.Card}>
                                <Select name="activityFilterId" title={Translations.EmployeeType} value={state.selectedActivityFilterId} onChange={this.handleChange}>
                                    {props.activityFilters.map(activityFilter =>
                                        <MenuItem key={activityFilter.id} value={activityFilter.id}>{activityFilter.title}</MenuItem>
                                    )}
                                </Select>
                            </FormField>
                        </Grid>
                    </GridRow>
                    <GridRow>
                        <Grid item sm={12}>
                            <FormField title={Translations.Unit}>
                                <TreeCheckboxDropdown
                                    required={true}
                                    selectedIds={state.selectedUnitIds}
                                    values={props.units}
                                    onChange={this.handleChangeUnitIds}
                                />
                            </FormField>
                        </Grid>
                    </GridRow>
                    <GridRow>
                        <Grid item sm={12}>
                            <FormField title={Translations.EmployeeType}>
                                <CheckboxDropdown
                                    required={true}
                                    selectedIds={state.selectedEmployeeTypeIds}
                                    toggleAll={true}
                                    values={props.employeeTypes}
                                    onChange={this.handleChangeEmployeeTypeIds}
                                />
                            </FormField>
                        </Grid>
                    </GridRow>
                    <GridRow>
                        <Grid item sm={12}>
                            <FormField title={Translations.JobTitle}>
                                <CheckboxDropdown
                                    required={true}
                                    toggleAll={true}
                                    selectedIds={state.selectedJobTitleIds}
                                    values={props.jobTitles}
                                    onChange={this.handleChangeJobTitleIds}
                                />
                            </FormField>
                        </Grid>
                    </GridRow>
                    {!!state.extraParameters &&
                        <Box sx={{ margin: "1rem -1rem 0" }}>
                            <TaskDynamicForm
                                taskTypeCols={props.report?.taskType?.formCols}
                                taskDataRowCols={state.extraParameters.cols}
                                onRefreshCols={this.handleRefreshParameters}
                            />
                        </Box>
                    }
                </Box>
                <FilterButtons
                    onCancel={undefined}
                    onClear={this.handleClear}
                    onSave={this.handleSave}
                />
            </Box>
        );
    }
}